import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import {
  ArrowLeft, BadgeCheck, Users, Sparkles, Compass, Target, Mic, ShieldCheck,
  Loader2, Save, Network, Inbox, Shield, Award, Download, Share2,
} from "lucide-react";
import VerifiedBadgeChip from "../components/instructor/VerifiedBadgeChip";
import AchievementBadgesPanel from "../components/instructor/AchievementBadgesPanel";
import LearnerStoriesStrip from "../components/instructor/LearnerStoriesStrip";
import ProfessionalStandardsPanel from "../components/instructor/ProfessionalStandardsPanel";
import SpecialtyPicker from "../components/instructor/SpecialtyPicker";
import { useAuth } from "../context/AuthContext";
import { trackEvent } from "../utils/analytics";
import { useTruthState } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";

const API = process.env.REACT_APP_BACKEND_URL;

const TEACHING_STYLES = [
  ["calm", "Calm"], ["structured", "Structured"], ["confidence_builder", "Confidence builder"],
  ["intensive", "Intensive"], ["patient", "Patient"], ["beginner_friendly", "Beginner friendly"],
];
const SPECIALIST_TAGS = [
  ["nervous_learners", "Nervous learners"], ["refresher_lessons", "Refresher lessons"],
  ["motorway_lessons", "Motorway lessons"], ["test_prep", "Test prep"], ["confidence_rebuild", "Confidence rebuild"],
];
const GOALS = [
  ["relocating", "Relocating to new area"], ["franchise_school", "Want franchise / school role"],
  ["overflow_pupils", "Want overflow pupils"], ["mentoring", "Want mentoring"],
  ["recruit_pdis", "Want to recruit PDIs"],
];

export default function InstructorPassport() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [areaInput, setAreaInput] = useState("");
  const truth = useTruthState();

  const load = async () => {
    try {
      await truth.run(
        async () => {
          const { data } = await axios.get(`${API}/api/instructor-passport`, { withCredentials: true });
          setData(data);
          setF({ ...data.passport });
        },
        { failedMessage: "Couldn't load passport." },
      );
    } catch { /* truth.run captured */ }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  if (!data || !f) return <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const toggle = (key, val) => {
    const arr = new Set(f[key] || []);
    arr.has(val) ? arr.delete(val) : arr.add(val);
    setF({ ...f, [key]: Array.from(arr) });
  };
  const addArea = () => {
    if (!areaInput.trim()) return;
    setF({ ...f, current_areas: Array.from(new Set([...(f.current_areas || []), areaInput.trim()])).slice(0, 10) });
    setAreaInput("");
  };
  const save = async () => {
    setSaving(true);
    try {
      await truth.run(
        async () => {
          const { data } = await axios.put(`${API}/api/instructor-passport`, {
            adi_status: f.adi_status, years_experience: f.years_experience,
            transmission: f.transmission, current_areas: f.current_areas,
            teaching_styles: f.teaching_styles, specialist_tags: f.specialist_tags,
            goals: f.goals, intro_message: f.intro_message,
          }, { withCredentials: true });
          setData(data); setF({ ...data.passport });
        },
        {
          completedMessage: "Passport saved.",
          failedMessage:    "Couldn't save passport.",
        },
      );
    } catch { /* truth.run captured */ }
    setSaving(false);
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-slate-950 pb-20">
      <header className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="inst-passport-back" onClick={() => goBack()} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"><ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-extrabold text-slate-900 dark:text-white">Professional Passport</h1>
            <p className="text-[11px] text-slate-500">{data.trust_notice}</p>
          </div>
          <button data-testid="inst-passport-network" onClick={() => nav("/network")} className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 flex items-center gap-1"><Network className="w-3 h-3" /> Network</button>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
        {/* Batch P1 · Passport Truth-State — canonical async receipt.
            Docked at the top so Passport actions land consistently.
            Failed states expose Retry + Dismiss. */}
        <TruthReceipt
          state={truth.state}
          message={truth.message}
          onRetry={f ? save : load}
          onDismiss={() => truth.setState(null)}
        />

        {/* Verified DVSA card — trust chip + link to upload */}
        <div data-testid="inst-verification-card" className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <Shield className="w-4 h-4" />
              <p className="text-xs font-extrabold uppercase tracking-wider">DVSA verification</p>
            </div>
            <VerifiedBadgeChip verification={data.verification} size="lg" />
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Upload your official ADI (green) or PDI (pink) card so learners and other instructors can see a trusted chip on your profile. Photos stay private — only admins see them.
          </p>
          <button
            data-testid="inst-passport-upload-badge"
            onClick={() => nav("/instructor/verification")}
            className="mt-2 w-full px-3 py-2 text-xs font-extrabold rounded-xl bg-slate-900 text-white hover:bg-slate-800 flex items-center justify-center gap-1"
          >
            <Shield className="w-3.5 h-3.5" /> {data.verification?.verified ? "Renew / replace card" : "Upload badge"}
          </button>
        </div>

        {/* Auto-computed achievement badges */}
        <section data-testid="inst-achievements" className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center gap-2 mb-3 text-slate-500 dark:text-slate-400">
            <Award className="w-4 h-4" />
            <p className="text-xs font-extrabold uppercase tracking-wider">Achievement badges</p>
          </div>
          <AchievementBadgesPanel achievements={data.achievements} showUnearned />
          <p className="mt-2 text-[10px] text-slate-400">Badges unlock automatically from verified platform activity — no self-claims or paid badges.</p>
        </section>

        {/* NDG Verified share-card — premium downloadable PNG for Wave 1 ambassadors */}
        {user?.id && data.verification?.verified && (
          <VerifiedShareCardPanel userId={user.id} userName={user.name} />
        )}

        {/* Professional Standards — derived from check-completion history */}
        <ProfessionalStandardsPanel mode="private" />

        {/* Verified teaching record (read-only) */}
        <div data-testid="inst-verified-record" className="rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/15 dark:to-teal-900/15 border border-emerald-200 dark:border-emerald-900/40 p-4">
          <div className="flex items-center gap-2 mb-2">
            <BadgeCheck className="w-5 h-5 text-emerald-600" />
            <p className="text-xs font-extrabold uppercase tracking-wider text-emerald-800 dark:text-emerald-200">Verified teaching record</p>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            <Metric label="Learners" value={data.teaching_record.learners_supported} />
            <Metric label="Mastery" value={data.teaching_record.mastery_completions} />
            <Metric label="Active" value={data.teaching_record.active_progression_count} />
            <Metric label="Passes supported" value={data.teaching_record.verified_passes_supported || 0} />
          </div>
          {data.teaching_record.latest_pass_date && (
            <p data-testid="inst-latest-pass" className="mt-2 text-[10px] text-emerald-700 dark:text-emerald-300">
              Most recent verified learner pass: {data.teaching_record.latest_pass_date}
            </p>
          )}
          <p className="mt-2 text-[10px] text-emerald-700 dark:text-emerald-400 leading-snug">Counted from platform records only — learner passes supported, never a pass rate.</p>
        </div>

        {/* Approved learner stories (social proof — moderated quotes only) */}
        <LearnerStoriesStrip stories={data.learner_stories} />

        <Section testid="section-credentials" icon={<ShieldCheck className="w-4 h-4" />} title="Credentials">
          <div className="flex gap-2">
            {[["adi", "ADI"], ["pdi", "PDI"]].map(([k, l]) => (
              <button key={k} data-testid={`cred-${k}`} onClick={() => setF({ ...f, adi_status: k })}
                className={`flex-1 px-3 py-2 text-xs font-bold rounded-lg border ${f.adi_status === k ? "bg-indigo-600 text-white border-indigo-600" : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700"}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Input testid="cred-years" label="Years" value={f.years_experience || ""} onChange={(v) => setF({ ...f, years_experience: parseInt(v) || 0 })} placeholder="0" type="number" />
            <div>
              <label className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">Transmission</label>
              <div className="mt-1 flex gap-1">
                {[["manual", "M"], ["auto", "A"], ["either", "Both"]].map(([k, l]) => (
                  <button key={k} data-testid={`cred-transmission-${k}`} onClick={() => setF({ ...f, transmission: k })}
                    className={`flex-1 px-2 py-1.5 text-xs font-bold rounded border ${f.transmission === k ? "bg-indigo-600 text-white border-indigo-600" : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700"}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-2">
            <label className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Current areas</label>
            <div className="flex flex-wrap gap-1 mt-1">
              {(f.current_areas || []).map((a) => (
                <span key={a} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                  {a}
                  <button onClick={() => setF({ ...f, current_areas: (f.current_areas || []).filter((x) => x !== a) })} className="text-slate-400">×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <input data-testid="cred-area-input" value={areaInput} onChange={(e) => setAreaInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addArea())} placeholder="e.g. Reading" className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white" />
              <button onClick={addArea} className="px-3 py-1.5 text-xs font-bold rounded-lg bg-slate-900 text-white hover:bg-slate-800">Add</button>
            </div>
          </div>
        </Section>

        <ChipSection testid="section-styles" icon={<Users className="w-4 h-4" />} title="Teaching style" options={TEACHING_STYLES} selected={f.teaching_styles || []} onToggle={(k) => toggle("teaching_styles", k)} tone="blue" />
        <ChipSection testid="section-specialist" icon={<Sparkles className="w-4 h-4" />} title="Specialist strengths" options={SPECIALIST_TAGS} selected={f.specialist_tags || []} onToggle={(k) => toggle("specialist_tags", k)} tone="purple" />
        <ChipSection testid="section-goals" icon={<Target className="w-4 h-4" />} title="Career goals" options={GOALS} selected={f.goals || []} onToggle={(k) => toggle("goals", k)} tone="emerald" />

        <Section testid="section-intro" icon={<Mic className="w-4 h-4" />} title="Intro message (public)">
          <textarea data-testid="intro-message" value={f.intro_message || ""} onChange={(e) => setF({ ...f, intro_message: e.target.value })} rows={4} maxLength={600}
            placeholder="A short, factual intro. Describe your teaching approach and focus areas. Avoid guaranteed-pass claims."
            className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white resize-none" />
          <p className="mt-1 text-[10px] text-slate-400">{(f.intro_message || "").length}/600 · Factual wording only.</p>
        </Section>

        <Section testid="section-specialisms" icon={<Sparkles className="w-4 h-4" />} title="Specialisms (public)">
          <PassportSpecialisms />
        </Section>

        <button data-testid="inst-passport-save" onClick={save} disabled={saving}
          className="w-full px-4 py-3 text-sm font-extrabold rounded-xl bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save passport"}
        </button>

        {/* Inbox link */}
        <button data-testid="transfer-inbox-link" onClick={() => nav("/instructor/transfers")}
          className="w-full mt-2 px-4 py-2.5 text-xs font-bold rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 flex items-center justify-center gap-2">
          <Inbox className="w-4 h-4" /> Transfer requests + outgoing handovers
        </button>
      </div>
    </div>
  );
}

function PassportSpecialisms() {
  const [list, setList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const truth = useTruthState();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/marketplace/my-profile`, { withCredentials: true });
        if (!cancelled) setList(data.specialties || []);
      } catch { /* fall through */ } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  const save = async (next) => {
    setList(next);
    setSaving(true);
    try {
      await truth.run(
        async () => {
          await axios.put(`${API}/api/marketplace/my-profile`, { specialties: next }, { withCredentials: true });
        },
        {
          completedMessage: "Specialisms saved.",
          failedMessage:    "Couldn't save specialisms.",
        },
      );
    } catch { /* truth.run captured */ }
    setSaving(false);
  };

  if (!loaded) return <p className="text-[11px] text-slate-400">Loading…</p>;
  return (
    <>
      <TruthReceipt state={truth.state} message={truth.message} onRetry={() => save(list)} onDismiss={() => truth.setState(null)} />
      <SpecialtyPicker value={list} onChange={save} testidPrefix="passport-spec" />
      {saving && <p className="text-[10px] text-slate-400 mt-2">Saving…</p>}
    </>
  );
}

function Section({ testid, icon, title, children }) {
  return (
    <section data-testid={testid} className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4">
      <div className="flex items-center gap-2 mb-3 text-slate-500 dark:text-slate-400">{icon}<p className="text-xs font-extrabold uppercase tracking-wider">{title}</p></div>
      {children}
    </section>
  );
}

function ChipSection({ testid, icon, title, options, selected, onToggle, tone }) {
  const ring = { blue: "bg-blue-600", purple: "bg-purple-600", emerald: "bg-emerald-600" }[tone];
  return (
    <Section testid={testid} icon={icon} title={title}>
      <div className="flex flex-wrap gap-1.5">
        {options.map(([k, l]) => (
          <button key={k} data-testid={`chip-${k}`} onClick={() => onToggle(k)}
            className={`px-2.5 py-1.5 text-xs font-bold rounded-full border ${selected.includes(k) ? `${ring} text-white border-transparent` : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700"}`}>{l}</button>
        ))}
      </div>
    </Section>
  );
}

function Input({ testid, label, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-white" />
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <p className="text-2xl font-extrabold text-emerald-900 dark:text-emerald-100">{value}</p>
      <p className="text-[10px] uppercase font-bold tracking-wider text-emerald-700 dark:text-emerald-300">{label}</p>
    </div>
  );
}

/**
 * NDG Verified Share-Card panel — preview + Download + native Share.
 * Card is rendered server-side as a 1080×1080 PNG; we just embed the URL.
 */
function VerifiedShareCardPanel({ userId, userName }) {
  const [busy, setBusy] = useState(false);
  const [tier, setTier] = useState(null);
  const truth = useTruthState();
  // Cache-buster keeps preview fresh after profile edits.
  const [v] = useState(() => Date.now());
  const cardUrl = `${API}/api/marketplace/verified-card/${userId}?v=${v}`;
  // Public referral link — homepage hit with ?ref=<user_id> fires share_card_referral.
  const refLink =
    typeof window !== "undefined"
      ? `${window.location.origin}/?ref=${userId}`
      : `https://nationaldrivingguild.co.uk/?ref=${userId}`;

  // Fetch tier once for analytics enrichment (best-effort; fails silently).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/standards/me`, { withCredentials: true });
        if (!cancelled) setTier((data?.tier || null));
      } catch { /* tier remains null */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const fireShareEvent = (action_type) => {
    trackEvent("landing_share_card_clicked", {
      user_id: userId,
      action_type,
      tier: tier || "unknown",
    });
  };

  const download = async () => {
    setBusy(true);
    try {
      await truth.run(
        async () => {
          const res = await fetch(cardUrl, { credentials: "include" });
          const blob = await res.blob();
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = `ndg-verified-${(userName || "member").toLowerCase().replace(/\s+/g, "-")}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(link.href);
          fireShareEvent("download");
        },
        {
          completedMessage: "Share-card downloaded.",
          failedMessage:    "Couldn't download right now.",
        },
      );
    } catch { /* truth.run captured */ }
    setBusy(false);
  };

  const share = async () => {
    if (typeof navigator === "undefined" || !navigator.share) {
      // Fallback — copy ref link to clipboard for older browsers.
      try {
        await truth.run(
          async () => {
            await navigator.clipboard.writeText(refLink);
            fireShareEvent("share");
          },
          {
            completedMessage: "Share link copied.",
            failedMessage:    "Couldn't share.",
          },
        );
      } catch { /* truth.run captured */ }
      return;
    }
    try {
      const res = await fetch(cardUrl);
      const blob = await res.blob();
      const file = new File([blob], "ndg-verified.png", { type: "image/png" });
      // Some platforms require a files-capable share; fall back to link share.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "NDG Verified Member", text: refLink });
      } else {
        await navigator.share({ title: "NDG Verified Member", url: refLink });
      }
      fireShareEvent("share");
    } catch (e) {
      // navigator.share user-cancellation is expected — silent.
      if (e?.name !== "AbortError") {
        truth.setState("failed", "Couldn't share.");
      }
    }
  };

  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  return (
    <section
      data-testid="verified-share-card-panel"
      className="rounded-2xl bg-gradient-to-br from-amber-50 via-white to-amber-50 dark:from-amber-900/15 dark:via-slate-800 dark:to-amber-900/15 border border-amber-200 dark:border-amber-700/40 p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Award className="w-4 h-4 text-amber-600" />
        <p className="text-xs font-extrabold uppercase tracking-wider text-amber-800 dark:text-amber-200">
          NDG Verified · Share-card
        </p>
      </div>
      <div
        data-testid="verified-share-card-preview"
        className="relative rounded-xl overflow-hidden bg-slate-950 border border-amber-200/60 dark:border-amber-700/40 mb-3"
        style={{ aspectRatio: "1 / 1" }}
      >
        <img
          src={cardUrl}
          alt="Your NDG Verified share-card"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <p className="text-[11px] text-slate-600 dark:text-slate-300 mb-3 leading-relaxed">
        A premium 1080×1080 PNG to post on WhatsApp, Instagram or Facebook. Sharing automatically
        adds your referral link — we'll track who you bring to the Guild.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid="verified-share-download"
          onClick={download}
          disabled={busy}
          className="px-3 py-2.5 text-xs font-extrabold rounded-xl bg-slate-900 text-white hover:bg-slate-800 flex items-center justify-center gap-1.5 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Download PNG
        </button>
        <button
          data-testid="verified-share-share"
          onClick={share}
          className="px-3 py-2.5 text-xs font-extrabold rounded-xl bg-amber-500 text-slate-900 hover:bg-amber-400 flex items-center justify-center gap-1.5"
        >
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
      </div>
      <div className="mt-2">
        <TruthReceipt state={truth.state} message={truth.message} onRetry={download} onDismiss={() => truth.setState(null)} />
      </div>
    </section>
  );
}
